import { inject, injectable } from "inversify";
import maplibregl from "maplibre-gl";
import * as THREE from "three";
import { Logger } from "@designboticteam/core";
import { MapLibreInstanceService } from "@designboticteam/maplibre";
import { CAMERA_CONTROL_SERVICE, CameraService, type ICameraControlService, OrbitCameraControlService, RENDERER_SERVICE } from "@designboticteam/workspace-3d";
import type { MapLibreRendererService } from "../MapLibreRendererService";
import type { IMapLibreCameraSyncService } from "./ICameraSyncService";

@injectable()
class MapToWorkspace3DCameraSyncService implements IMapLibreCameraSyncService {
	public readonly syncDirection = "map-to-workspace-3d" as const; // TODO: It would be nice to git rid of this
	public readonly serviceName = "🗺️ MapLibreToWorkspace3DCameraSyncService";
	private readonly logger = new Logger(`🔗 CONNECTOR | ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#6644aa",
	});

	constructor(
		@inject(CAMERA_CONTROL_SERVICE) private cameraControlService: ICameraControlService,
		@inject(CameraService) private cameraService: CameraService,
		@inject(MapLibreInstanceService) private mapDisplayService: MapLibreInstanceService,
		@inject(RENDERER_SERVICE) private mapLibreRendererService: MapLibreRendererService,
	) {
		this.logger.log("constructed");
	}

	public initialize(): void {
		this.cameraControlService.disable();
		this.mapLibreRendererService.onBeforeRender.subscribe(this.synchronizeCamera);
		this.cameraControlService.onCameraControlsStatusChanged.subscribe(this.updateMapControlsState);
		this.logger.log("initialized");
	}

	public dispose(): void {
		this.mapLibreRendererService.onBeforeRender.unsubscribe(this.synchronizeCamera);
		this.cameraControlService.onCameraControlsStatusChanged.subscribe(this.updateMapControlsState);
		this.logger.log("disposed");
	}

	public updateMapControlsState = ({ isEnabled }: { isEnabled: boolean }) => {
		if (isEnabled) {
			this.mapDisplayService.map.dragPan.enable();
			this.mapDisplayService.map.dragRotate.enable();
			this.mapDisplayService.map.scrollZoom.enable();
			this.mapDisplayService.map.touchZoomRotate.enable();
			this.mapDisplayService.map.keyboard.enable();

			if (this.cameraControlService instanceof OrbitCameraControlService) {
				this.cameraControlService.controls.enabled = false;
			}
		} else {
			this.mapDisplayService.map.dragPan.disable();
			this.mapDisplayService.map.dragRotate.disable();
			this.mapDisplayService.map.scrollZoom.disable();
			this.mapDisplayService.map.touchZoomRotate.disable();
			this.mapDisplayService.map.keyboard.disable();
		}
	};

	/**
	 * Synchronizes Three.js camera from MapLibre's projection matrix.
	 *
	 * Gives Three.js the real camera position while keeping the rendering result identical.
	 *
	 * 1. Get camera LngLat + altitude from MapLibre → convert to mercator [0,1] via MercatorCoordinate
	 * 2. Convert to local space: eyeLocal = cameraPosMercator × modelMatrix^(-1)
	 * 3. Put the real position into matrixWorld: matrixWorld = Translation(eyeLocal)
	 * 4. Bake matrixWorld into projectionMatrix so that matrixWorld and matrixWorldInverse cancel out:
	 *
	 * clip = projectionMatrix × matrixWorldInverse × vertex
	 *      = (MVP × matrixWorld) × matrixWorld⁻¹ × vertex
	 *      = MVP × vertex
	 *
	 * Same clip position as before — but now matrixWorld holds the real camera position,
	 * so raycasting, LOD etc. all get the correct value.
	 */
	public synchronizeCamera = ({ cameraProjectionMatrix, modelMatrix }: { cameraProjectionMatrix: THREE.Matrix4; modelMatrix: THREE.Matrix4 }): void => {
		const mapCameraPosition = this.getMapCameraMercatorPosition();

		const modelMatrixInverse = modelMatrix.clone().invert();
		const eyeLocal = mapCameraPosition.applyMatrix4(modelMatrixInverse);

		const camera = this.cameraService.camera;
		camera.matrixAutoUpdate = false;
		camera.matrixWorld.makeTranslation(eyeLocal.x, eyeLocal.y, eyeLocal.z);
		camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
		camera.projectionMatrix.copy(cameraProjectionMatrix).multiply(camera.matrixWorld);
		camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();
	};

	private getMapCameraMercatorPosition(): THREE.Vector3 {
		const map = this.mapDisplayService.map;
		const { lng, lat } = map.transform.getCameraLngLat();
		const altitude = map.transform.getCameraAltitude();
		const { x, y, z } = maplibregl.MercatorCoordinate.fromLngLat([lng, lat], altitude);
		return new THREE.Vector3(x, y, z);
	}
}

export { MapToWorkspace3DCameraSyncService };
